import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/common/Layout';
import Loading from '../components/common/Loading';
import { finanzasService } from '../services/finanzas';
import toast from 'react-hot-toast';
import { DollarSign, TrendingDown, AlertTriangle, XCircle, Package, ExternalLink, Upload, X, Sparkles, RefreshCw } from 'lucide-react';

const ESTADOS = [
    { value: '', label: 'Todos los estados' },
    { value: 'vigente', label: 'Vigente' },
    { value: 'por_vencer', label: 'Por vencer (≤ 90 días)' },
    { value: 'vencido', label: 'Vencido' },
    { value: 'sin_datos', label: 'Sin datos financieros' },
];

const badgeEstado = (estado) => {
    switch (estado) {
        case 'vigente': return 'bg-green-100 text-green-800';
        case 'por_vencer': return 'bg-yellow-100 text-yellow-800';
        case 'vencido': return 'bg-red-100 text-red-800';
        default: return 'bg-gray-100 text-gray-600';
    }
};

const textoEstado = (estado) => {
    switch (estado) {
        case 'vigente': return 'Vigente';
        case 'por_vencer': return 'Por vencer';
        case 'vencido': return 'Vencido';
        default: return 'Sin datos';
    }
};

const tienePrecioIA = (r) => !!r.ia_precio_actualizado_en || (r.ia_valor_mercado !== null && r.ia_valor_mercado !== undefined);

const formatMoney = (n) => (n === null || n === undefined ? '—' : `S/ ${Number(n).toLocaleString('es-PE', { minimumFractionDigits: 2 })}`);

const FinanzasPage = () => {
    const navigate = useNavigate();
    const [registros, setRegistros] = useState([]);
    const [resumen, setResumen] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filtroEstado, setFiltroEstado] = useState('');
    const [subiendo, setSubiendo] = useState(false);
    const [resultadoImportacion, setResultadoImportacion] = useState(null);
    const inputExcelRef = useRef(null);
    const [estimandoIds, setEstimandoIds] = useState(new Set());
    const [loteIA, setLoteIA] = useState(null); // { estimados, pendientes } mientras corre el lote
    const detenerLoteRef = useRef(false);

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const [lista, res] = await Promise.all([
                finanzasService.listarDepreciacion(filtroEstado ? { estado: filtroEstado } : {}),
                finanzasService.resumen(),
            ]);
            setRegistros(lista);
            setResumen(res);
        } catch (err) {
            toast.error('Error al cargar la información financiera');
        } finally {
            setLoading(false);
        }
    }, [filtroEstado]);

    useEffect(() => { cargar(); }, [cargar]);

    // Copia los precios estimados a las filas ya cargadas, sin recargar la tabla.
    const aplicarEstimaciones = (estimados) => {
        const porId = Object.fromEntries(estimados.map((e) => [e.activo_id, e]));
        setRegistros((prev) => prev.map((r) => (porId[r.activo_id] ? { ...r, ...porId[r.activo_id] } : r)));
    };

    const estimarActivo = async (activoId) => {
        setEstimandoIds((prev) => new Set(prev).add(activoId));
        try {
            const resultado = await finanzasService.estimarPrecioMercado([activoId]);
            aplicarEstimaciones(resultado.estimados);
            if (resultado.sin_resultado.length) toast.error('La IA no devolvió una estimación para este activo');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Error al estimar el precio de mercado');
        } finally {
            setEstimandoIds((prev) => {
                const nuevo = new Set(prev);
                nuevo.delete(activoId);
                return nuevo;
            });
        }
    };

    // Recorre por lotes de 15 todos los activos que aún no tienen precio estimado.
    const estimarPendientes = async () => {
        detenerLoteRef.current = false;
        setLoteIA({ estimados: 0, pendientes: null });
        let total = 0;
        try {
            while (!detenerLoteRef.current) {
                const resultado = await finanzasService.estimarPrecioMercado();
                const procesados = resultado.estimados.length + resultado.sin_resultado.length;
                total += resultado.estimados.length;
                aplicarEstimaciones(resultado.estimados);
                setLoteIA({ estimados: total, pendientes: resultado.pendientes });
                if (resultado.pendientes === 0 || procesados === 0) break;
            }
            toast.success(`Precio de mercado estimado para ${total} activos`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Error al estimar precios de mercado');
        } finally {
            setLoteIA(null);
        }
    };

    const handleExcelNetsuite = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setSubiendo(true);
        try {
            const resultado = await finanzasService.importarNetsuite(file);
            setResultadoImportacion(resultado);
            toast.success(`${resultado.activos_actualizados} activos actualizados desde NetSuite`);
            cargar();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Error al procesar el Excel');
        } finally {
            setSubiendo(false);
        }
    };

    return (
        <Layout>
            <div className="p-6 max-w-7xl mx-auto">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
                    <div className="flex items-center space-x-3">
                        <div className="bg-emerald-100 p-2.5 rounded-xl">
                            <DollarSign className="h-6 w-6 text-emerald-700" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">Gestión Financiera</h1>
                            <p className="text-sm text-gray-500">Depreciación de equipos y fechas de reemplazo (método lineal)</p>
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                        <input
                            ref={inputExcelRef}
                            type="file"
                            accept=".xlsx"
                            className="hidden"
                            onChange={handleExcelNetsuite}
                        />
                        <button
                            onClick={() => inputExcelRef.current?.click()}
                            disabled={subiendo}
                            title="Excel de conciliación de activos fijos: se cruza la columna ID con el ID NetSuite de cada activo"
                            className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60"
                        >
                            <Upload className="h-4 w-4 mr-2" />
                            {subiendo ? 'Procesando Excel...' : 'Subir Excel NetSuite'}
                        </button>
                        {loteIA ? (
                            <button
                                onClick={() => { detenerLoteRef.current = true; }}
                                className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700"
                            >
                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                {`Estimando... ${loteIA.estimados}${loteIA.pendientes !== null ? ` (faltan ${loteIA.pendientes})` : ''} · Detener`}
                            </button>
                        ) : (
                            <button
                                onClick={estimarPendientes}
                                title="Estima con IA el valor de mercado de los activos que aún no lo tienen (por lotes de 15)"
                                className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700"
                            >
                                <Sparkles className="h-4 w-4 mr-2" />
                                Estimar precios con IA
                            </button>
                        )}
                        <button
                            onClick={() => navigate('/activos')}
                            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50"
                        >
                            <Package className="h-4 w-4 mr-2" />
                            Editar datos / cargar plantilla en Activos
                            <ExternalLink className="h-3.5 w-3.5 ml-2" />
                        </button>
                    </div>
                </div>

                {/* Resultado de la última carga del Excel de NetSuite */}
                {resultadoImportacion && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6 text-sm">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="font-semibold text-emerald-900">
                                    Excel NetSuite procesado (hoja "{resultadoImportacion.hoja}")
                                </p>
                                <p className="text-emerald-800 mt-1">
                                    {resultadoImportacion.filas_leidas} filas leídas ·{' '}
                                    <strong>{resultadoImportacion.activos_actualizados}</strong> activos actualizados ·{' '}
                                    {resultadoImportacion.ids_no_encontrados} IDs sin activo en Argus ·{' '}
                                    {resultadoImportacion.activos_con_id_sin_fila} activos con ID NetSuite que no están en el Excel
                                </p>
                                {resultadoImportacion.ejemplos_no_encontrados?.length > 0 && (
                                    <p className="text-xs text-emerald-700 mt-2 break-words">
                                        IDs no encontrados (primeros {resultadoImportacion.ejemplos_no_encontrados.length}):{' '}
                                        {resultadoImportacion.ejemplos_no_encontrados.join(', ')}
                                    </p>
                                )}
                            </div>
                            <button
                                onClick={() => setResultadoImportacion(null)}
                                className="text-emerald-700 hover:text-emerald-900 ml-4"
                                aria-label="Cerrar"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                )}

                {/* Resumen */}
                {resumen && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                        <div className="bg-white rounded-xl border border-gray-200 p-4">
                            <p className="text-xs text-gray-500 uppercase font-semibold">Valor de compra</p>
                            <p className="text-lg font-bold text-gray-900 mt-1">{formatMoney(resumen.valor_compra_total)}</p>
                        </div>
                        <div className="bg-white rounded-xl border border-gray-200 p-4">
                            <p className="text-xs text-gray-500 uppercase font-semibold">Valor actual</p>
                            <p className="text-lg font-bold text-gray-900 mt-1">{formatMoney(resumen.valor_actual_total)}</p>
                        </div>
                        <div className="bg-white rounded-xl border border-gray-200 p-4">
                            <p className="text-xs text-gray-500 uppercase font-semibold flex items-center">
                                <TrendingDown className="h-3.5 w-3.5 mr-1" /> Depreciado
                            </p>
                            <p className="text-lg font-bold text-gray-900 mt-1">{formatMoney(resumen.depreciacion_acumulada_total)}</p>
                        </div>
                        <div className="bg-white rounded-xl border border-gray-200 p-4">
                            <p className="text-xs text-gray-500 uppercase font-semibold flex items-center">
                                <AlertTriangle className="h-3.5 w-3.5 mr-1 text-yellow-500" /> Por vencer
                            </p>
                            <p className="text-lg font-bold text-gray-900 mt-1">{resumen.activos_por_vencer}</p>
                        </div>
                        <div className="bg-white rounded-xl border border-gray-200 p-4">
                            <p className="text-xs text-gray-500 uppercase font-semibold flex items-center">
                                <XCircle className="h-3.5 w-3.5 mr-1 text-red-500" /> Vencidos
                            </p>
                            <p className="text-lg font-bold text-gray-900 mt-1">{resumen.activos_vencidos}</p>
                        </div>
                        <div className="bg-white rounded-xl border border-gray-200 p-4">
                            <p className="text-xs text-gray-500 uppercase font-semibold">Sin datos</p>
                            <p className="text-lg font-bold text-gray-900 mt-1">{resumen.activos_sin_datos}</p>
                        </div>
                    </div>
                )}

                {/* Filtro */}
                <div className="flex items-center space-x-2 mb-4">
                    <select
                        value={filtroEstado}
                        onChange={(e) => setFiltroEstado(e.target.value)}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                        {ESTADOS.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
                    </select>
                </div>

                {/* Tabla */}
                {loading ? (
                    <Loading message="Calculando depreciación..." />
                ) : (
                    <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Activo</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Categoría / Ubicación</th>
                                    <th className="px-4 py-3 text-right font-semibold text-gray-600">Precio compra</th>
                                    <th className="px-4 py-3 text-right font-semibold text-gray-600">Valor actual</th>
                                    <th className="px-4 py-3 text-right font-semibold text-gray-600">Precio mercado (IA)</th>
                                    <th className="px-4 py-3 text-right font-semibold text-gray-600">% Depreciado</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Fecha reemplazo</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Estado</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {registros.length === 0 && (
                                    <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Sin registros</td></tr>
                                )}
                                {registros.map((r) => (
                                    <tr key={r.activo_id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3">
                                            <div className="font-semibold text-gray-900">{r.name}</div>
                                            <div className="text-xs text-gray-400">
                                                {r.code}{r.id_netsuite ? ` · ${r.id_netsuite}` : ''}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-gray-600">
                                            <div>{r.categoria || '—'}</div>
                                            <div className="text-xs text-gray-400">{r.ubicacion_nombre || '—'}</div>
                                        </td>
                                        <td className="px-4 py-3 text-right text-gray-900">
                                            {formatMoney(r.precio_compra)}
                                            {r.fuente === 'netsuite' && (
                                                <div className="text-xs text-emerald-600 font-semibold">NetSuite</div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-right font-semibold text-gray-900">{formatMoney(r.valor_actual)}</td>
                                        <td className="px-4 py-3 text-right" title={r.ia_precio_comentario || ''}>
                                            {tienePrecioIA(r) ? (
                                                <>
                                                    <div className="font-semibold text-violet-700">{formatMoney(r.ia_valor_mercado)}</div>
                                                    {r.ia_precio_nuevo !== null && r.ia_precio_nuevo !== undefined && (
                                                        <div className="text-xs text-gray-400">nuevo: {formatMoney(r.ia_precio_nuevo)}</div>
                                                    )}
                                                </>
                                            ) : null}
                                            <button
                                                onClick={() => estimarActivo(r.activo_id)}
                                                disabled={estimandoIds.has(r.activo_id) || !!loteIA}
                                                className="inline-flex items-center text-xs text-violet-600 hover:text-violet-800 disabled:opacity-50 mt-0.5"
                                            >
                                                {estimandoIds.has(r.activo_id) ? (
                                                    <><RefreshCw className="h-3 w-3 mr-1 animate-spin" /> Estimando...</>
                                                ) : tienePrecioIA(r) ? (
                                                    <><RefreshCw className="h-3 w-3 mr-1" /> Recalcular</>
                                                ) : (
                                                    <><Sparkles className="h-3 w-3 mr-1" /> Estimar</>
                                                )}
                                            </button>
                                        </td>
                                        <td className="px-4 py-3 text-right text-gray-600">
                                            {r.porcentaje_depreciado !== null && r.porcentaje_depreciado !== undefined
                                                ? `${r.porcentaje_depreciado}%` : '—'}
                                        </td>
                                        <td className="px-4 py-3 text-gray-600">
                                            {r.fecha_reemplazo || '—'}
                                            {r.dias_para_reemplazo !== null && r.dias_para_reemplazo !== undefined && (
                                                <div className="text-xs text-gray-400">
                                                    {r.dias_para_reemplazo >= 0
                                                        ? `en ${r.dias_para_reemplazo} días`
                                                        : `vencido hace ${Math.abs(r.dias_para_reemplazo)} días`}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badgeEstado(r.estado)}`}>
                                                {textoEstado(r.estado)}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default FinanzasPage;
